/**
 * The reasonableness test, said where the amount is entered.
 *
 * WHY IT MOVED HERE. This is the single question that decides whether a granny flat arrangement is a
 * clean transfer or a five-year penalty, and it was explained only inside the floating
 * "What's happening" panel. A family could type $600,000 into the box above and see nothing at all
 * about the assessed value it is being measured against — until they opened a panel elsewhere on the
 * screen and found the right card.
 *
 * WHAT THE TEST IS. Centrelink puts a value on the lifetime right you receive: the combined
 * partnered pension rate times an age factor, or the value of the property or dwelling you provide,
 * whichever applies. Give up to that and none of it is a gift — granny flat interests are a
 * deliberate exception to the ordinary gifting limits. Give more and the excess is deprivation:
 * counted as your asset, and deemed to earn income, for five years from the start date.
 *
 * AND THE THING FAMILIES GET WRONG. When the former home is KEPT, the gift result is usually not
 * the main event. A house nobody lives in stops being exempt the moment you move out, and on most
 * cases that costs more pension than any deprivation does. The note says so where it applies,
 * because a family reading "none of this counts as a gift" would otherwise reasonably conclude the
 * arrangement is means-test neutral.
 *
 * Every figure is passed in from the page's own state — the same values the plan summary and the
 * projections use. Nothing is re-derived here.
 */
import React from 'react';
import { FieldNote } from '../../components/narrative/PlainWords.tsx';
import { formatCurrency } from '../../utils/index.ts';

export interface GrannyFlatArrangementNoteProps {
    /** Total given for the right: cash + property transferred + construction. */
    entryContribution: number;
    /** What Centrelink values the right at — the reasonable value, or the property/build provided. */
    interestValue: number;
    reasonableValue: number;
    /** The excess over the assessed value, counted for five years. */
    deprivation: number;
    keepingHome: boolean;
    formerHomeValue: number;
}

export default function GrannyFlatArrangementNote({
    entryContribution,
    interestValue,
    reasonableValue,
    deprivation,
    keepingHome,
    formerHomeValue,
}: GrannyFlatArrangementNoteProps) {
    if (!(entryContribution > 0)) return null;

    const gifted = Math.round(deprivation) > 0;
    const providesProperty = interestValue > reasonableValue;

    return (
        <>
            <FieldNote
                tone={gifted ? 'warn' : 'good'}
                heading={
                    gifted
                        ? `${formatCurrency(deprivation)} of this counts as a gift`
                        : 'None of this counts as a gift'
                }
            >
                <p>
                    Centrelink values the lifetime right you receive at{' '}
                    <strong>{formatCurrency(interestValue)}</strong>
                    {providesProperty
                        ? ' — the property or dwelling you are providing.'
                        : ' — the reasonable value, worked out from the combined partnered pension rate and your age.'}{' '}
                    You are giving <strong>{formatCurrency(entryContribution)}</strong>.
                </p>
                {gifted ? (
                    <p className="mt-1.5">
                        The <strong>{formatCurrency(deprivation)}</strong> above that assessed value is treated
                        as <strong>deprivation</strong>: Centrelink counts it as your asset, and deems it to
                        earn income, for <strong>five years</strong> from the start date — even though the
                        money has gone. Keeping what you give close to the assessed value avoids it entirely.
                    </p>
                ) : (
                    <p className="mt-1.5">
                        What you give sits within the assessed value, so none of it is deprivation. Granny flat
                        interests are a deliberate exception to the ordinary gifting limits when the amount is
                        reasonable.
                    </p>
                )}
            </FieldNote>

            {/*
              * The correction families most need. Without it, "none of this counts as a gift" reads
              * as "this is means-test neutral", which is the opposite of what usually happens.
              */}
            {keepingHome && formerHomeValue > 0 && (
                <FieldNote tone="warn" heading="The gift question is usually not the main event">
                    Whatever the gift result, the{' '}
                    <strong>{formatCurrency(formerHomeValue)}</strong> home you are keeping stops being exempt
                    the moment nobody in the household lives in it. From then it is counted like any other
                    asset — and on most cases that moves the Age Pension further than any deprivation does.
                    Selling it, or moving in without keeping it, is the bigger lever.
                </FieldNote>
            )}
        </>
    );
}
